// ============================================================
// Auth Context
// ============================================================
// Wraps Zustand auth store in React Context for provider pattern.
// Provides login/logout + initializes school context on login.

import { createContext, useCallback, useEffect, useState } from 'react';
import useAuthStore from '@/store/authStore';
import useSchoolStore from '@/store/schoolStore';
import authService from '@/services/authService';
import schoolService from '@/services/schoolService';
import { ROLES } from '@/constants/roles';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const {
    user,
    token,
    isAuthenticated,
    login: storeLogin,
    logout: storeLogout,
    setUser,
  } = useAuthStore();

  const { setSchools, setCurrentSchool, clearSchoolData } = useSchoolStore();
  const [loading, setLoading] = useState(true);

  // ── Initialize auth on mount ───────────────────────────
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const res = await authService.getCurrentUser(token);
          setUser(res.data);
          await initializeSchools(res.data);
        } catch {
          storeLogout();
          clearSchoolData();
        }
      }
      setLoading(false);
    };
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initialize school context for a user ───────────────
  const initializeSchools = useCallback(
    async (userData) => {
      try {
        if (userData.role === ROLES.SUPER_ADMIN) {
          // Super Admin operates globally, no specific school selection
          setSchools([]);
          setCurrentSchool(null);
        } else if (userData.role === ROLES.ORGANIZATION_ADMIN || String(userData.role).toUpperCase() === 'ORGANIZATION_ADMIN') {
          const res = await schoolService.getSchools(userData.organization_id);
          setSchools(res.data);
          // Find matching persisted school or default to first school with modules
          const currentSchoolState = useSchoolStore.getState().currentSchool;
          let activeSchool = res.data.find((s) => s.id === currentSchoolState?.id);
          if (!activeSchool || !Array.isArray(activeSchool.school_modules) || activeSchool.school_modules.length === 0) {
            const schoolWithModules = res.data.find((s) => Array.isArray(s.school_modules) && s.school_modules.length > 0);
            if (schoolWithModules) {
              activeSchool = schoolWithModules;
            } else if (!activeSchool) {
              activeSchool = res.data[0];
            }
          }
          if (activeSchool) {
            setCurrentSchool(activeSchool);
          }
        } else if (userData.school) {
          // If the API provided the full school object, format it correctly
          const schoolData = {
            id: userData.school.id,
            name: userData.school.school_name,
            code: userData.school.school_code,
            logo: userData.school.school_logo,
            email: userData.school.school_email,
            phone: userData.school.school_phone,
            ...userData.school
          };
          setSchools([schoolData]);
          setCurrentSchool(schoolData);
        } else if (userData.school_id || userData.schoolId) {
          const idToFetch = userData.school_id || userData.schoolId;
          const res = await schoolService.getSchoolById(idToFetch);
          setSchools([res.data]);
          setCurrentSchool(res.data);
        }
      } catch (error) {
        console.error('Failed to initialize schools:', error);
      }
    },
    [setSchools, setCurrentSchool]
  );

  // ── Login ──────────────────────────────────────────────
  const login = useCallback(
    async (email, password) => {
      const res = await authService.login(email, password);
      const modulesPayload = res.data?.modules || res.modules || [];
      const qrScannerEnabledPayload = res.data?.qr_scanner_enabled ?? res.data?.user?.qr_scanner_enabled ?? false;
      storeLogin(res.data.user, res.data.token, modulesPayload, qrScannerEnabledPayload);
      await initializeSchools(res.data.user);
      return res;
    },
    [storeLogin, initializeSchools]
  );

  // ── Logout ─────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Logout even if API fails
    } finally {
      storeLogout();
      clearSchoolData();
    }
  }, [storeLogout, clearSchoolData]);

  const value = {
    user,
    token,
    isAuthenticated,
    isInitialized: !loading,
    loading,
    login,
    logout,
    role: user?.role || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
